import axios from 'axios';

// Sanitize: strip trailing dots (DNS root) and trailing slashes from env var
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\.+\/*$/, '').replace(/\/+$/, '');

const crmAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

// Handle 401 errors by redirecting to login
// ONLY redirect if we're on admin/CRM routes (not portal, club, etc.)
crmAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const pathname = window.location.pathname;

      // Don't redirect if already on any login page
      if (pathname.includes('/login')) {
        return Promise.reject(error);
      }

      // Don't redirect from portal, club, mill, or store routes - those have their own handlers
      if (pathname.startsWith('/portal') || pathname.startsWith('/club') ||
          pathname.startsWith('/mill') || pathname.startsWith('/store')) {
        return Promise.reject(error);
      }

      // Only redirect to /login from admin/CRM routes
      localStorage.removeItem('rbf_user_data');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Lead Interfaces
export interface Lead {
  id: number;
  business_name: string;
  business_type: string;
  business_type_display: string;
  website?: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  region?: string;
  distance_from_kettle_falls?: number;
  contact_name: string;
  contact_title?: string;
  phone: string;
  email?: string;
  preferred_contact_method: string;
  preferred_contact_method_display?: string;
  status: string;
  status_display: string;
  source: string;
  source_display: string;
  assigned_to?: string;
  assigned_to_id?: number;
  estimated_monthly_volume?: string;
  interested_products?: string;
  notes?: string;
  last_contact_date?: string;
  next_follow_up?: string;
  is_follow_up_overdue: boolean;
  days_since_contact?: number;
  converted_to_customer: boolean;
  customer_id?: number;
  customer_name?: string;
  converted_date?: string;
  created_at: string;
  created_by?: string;
  updated_at?: string;
  activities?: LeadActivity[];
  tasks?: CRMTask[];
}

export interface LeadActivity {
  id: number;
  activity_type: string;
  activity_type_display: string;
  subject: string;
  notes: string;
  outcome?: string;
  created_by?: string;
  created_at: string;
  follow_up_needed: boolean;
  follow_up_date?: string;
  lead_id?: number;
  lead_name?: string;
}

export interface CRMTask {
  id: number;
  title: string;
  description?: string;
  lead_id?: number;
  lead_name?: string;
  customer_id?: number;
  customer_name?: string;
  assigned_to?: string;
  assigned_to_id?: number;
  assigned_by?: string;
  due_date: string;
  priority: string;
  priority_display?: string;
  completed: boolean;
  completed_at?: string;
  is_overdue: boolean;
  created_at: string;
}

export interface CRMUser {
  id: number;
  username: string;
  full_name: string;
  initials: string;
}

export interface CRMStats {
  total_leads: number;
  new_this_month: number;
  in_pipeline: number;
  converted_this_month: number;
  follow_ups_this_week: number;
  overdue_follow_ups: number;
  leads_by_status: Record<string, number>;
  leads_by_source: Record<string, number>;
  /** Open tasks with no owner - surfaced so unowned work is visible. */
  unassigned_tasks?: number;
}

// Lead API
export const leadAPI = {
  // List leads with filters
  getAll: (params?: {
    status?: string;
    assigned_to?: number;
    region?: string;
    source?: string;
    business_type?: string;
    search?: string;
    sort?: string;
  }) => crmAPI.get<{ count: number; results: Lead[] }>('/crm/leads/', { params }),

  // Get single lead
  getById: (id: number) => crmAPI.get<Lead>(`/crm/leads/${id}/`),

  // Create lead
  create: (data: Partial<Lead>) => crmAPI.post<{ success: boolean; id: number }>('/crm/leads/', data),

  // Update lead
  update: (id: number, data: Partial<Lead>) => crmAPI.put<{ success: boolean }>(`/crm/leads/${id}/`, data),

  // Delete lead
  delete: (id: number) => crmAPI.delete(`/crm/leads/${id}/`),

  // Add activity
  addActivity: (leadId: number, data: {
    activity_type: string;
    subject: string;
    notes?: string;
    outcome?: string;
    follow_up_needed?: boolean;
    follow_up_date?: string;
  }) => crmAPI.post(`/crm/leads/${leadId}/add_activity/`, data),

  // Convert to customer
  convertToCustomer: (leadId: number, data?: {
    name?: string;
    customer_type?: string;
  }) => crmAPI.post(`/crm/leads/${leadId}/convert_to_customer/`, data),

  // Get pipeline view
  getPipeline: () => crmAPI.get<Record<string, Lead[]>>('/crm/leads/pipeline/'),

  // Get CRM stats
  getStats: () => crmAPI.get<CRMStats>('/crm/leads/stats/'),

  // Get upcoming follow-ups
  getUpcomingFollowUps: () => crmAPI.get<Lead[]>('/crm/leads/upcoming_follow_ups/'),

  // Get recent activity
  getRecentActivity: () => crmAPI.get<LeadActivity[]>('/crm/leads/recent_activity/'),
};

// Task API
export const taskAPI = {
  // List tasks
  getAll: (params?: {
    my_tasks?: boolean;
    assigned_to?: number;
    /** Tasks nobody owns - a call whose staff member could not be resolved. */
    unassigned?: boolean;
    completed?: boolean;
    overdue?: boolean;
  }) => crmAPI.get<{ count: number; results: CRMTask[] }>('/crm/tasks/', { params }),

  // Create task
  create: (data: {
    title: string;
    description?: string;
    lead_id?: number;
    customer_id?: number;
    assigned_to?: number;
    due_date: string;
    priority?: string;
  }) => crmAPI.post<{ success: boolean; id: number }>('/crm/tasks/', data),

  // Update task
  update: (id: number, data: Partial<CRMTask>) => crmAPI.put<{ success: boolean }>(`/crm/tasks/${id}/`, data),

  // Complete task
  complete: (id: number) => crmAPI.post(`/crm/tasks/${id}/complete/`),

  // Uncomplete task
  uncomplete: (id: number) => crmAPI.post(`/crm/tasks/${id}/uncomplete/`),

  // Delete task
  delete: (id: number) => crmAPI.delete(`/crm/tasks/${id}/`),

  // Get my tasks
  getMyTasks: () => crmAPI.get<CRMTask[]>('/crm/tasks/my_tasks/'),
};

// User API for assignments
export const crmUserAPI = {
  getAll: () => crmAPI.get<CRMUser[]>('/crm/users/'),
};

// ═══════════════════════════════════════════════════════════════════════════════
// NEW CONTACT TRACKING INTERFACES AND API
// ═══════════════════════════════════════════════════════════════════════════════

export interface ContactTask {
  id: number;
  customer_id: number;
  customer_name: string;
  customer_phone?: string;
  customer_email?: string;
  customer_type?: string;
  customer_type_display?: string;
  customer_city?: string;
  customer_state?: string;
  task_type: string;
  task_type_display: string;
  title: string;
  description?: string;
  assigned_to?: string;
  assigned_to_id?: number;
  due_date: string;
  priority: number;
  is_completed: boolean;
  completed_at?: string;
  related_order_id?: number;
  related_order_number?: string;
  contact_log_id?: number;
  created_at: string;
  is_overdue: boolean;
  // Extra fields from Monday view
  talking_points?: string;
  last_contacted_at?: string;
  preferred_contact_method?: string;
  best_time_to_contact?: string;
  days_since_contact?: number;
  spouse_name?: string;
  children_info?: string;
  pets_info?: string;
  hobbies_interests?: string;
  birthday?: string;
}

export interface ContactLog {
  id: number;
  customer_id: number;
  customer_name?: string;
  contact_type: string;
  contact_type_display: string;
  contacted_by?: string;
  contacted_by_id?: number;
  contacted_at: string;
  subject?: string;
  notes: string;
  email_subject?: string;
  email_body?: string;
  follow_up_needed: boolean;
  follow_up_date?: string;
  follow_up_notes?: string;
  follow_up_completed: boolean;
}

export interface RelationshipReminder {
  id: number;
  customer_id: number;
  customer_name?: string;
  reminder_type: string;
  reminder_type_display: string;
  title: string;
  description?: string;
  reminder_date: string;
  is_recurring: boolean;
  assigned_to?: string;
  assigned_to_id?: number;
  is_completed: boolean;
  completed_at?: string;
  created_at: string;
}

export interface NoContactCustomer {
  customer_id: number;
  customer_name: string;
  customer_type: string;
  customer_type_display: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  city?: string;
  state?: string;
  last_contacted_at?: string;
  days_since_contact: number;
  last_contacted_by?: string;
  last_order_date?: string;
  last_order_number?: string;
  talking_points?: string;
  preferred_contact_method?: string;
  best_time_to_contact?: string;
  spouse_name?: string;
  children_info?: string;
  pets_info?: string;
  hobbies_interests?: string;
  birthday?: string;
  urgency: 'critical' | 'warning' | 'normal';
}

export interface MondayDigestPreview {
  week_of: string;
  verse: {
    text: string;
    reference: string;
  };
  contact_tasks: ContactTask[];
  birthdays: {
    name: string;
    date: string;
    customer_name: string;
    is_spouse: boolean;
  }[];
  show_mill_data: boolean;
  mill_data?: {
    orders_this_week: number;
    total_lbs_to_produce: string;
  };
}

export interface RelationshipUpdate {
  spouse_name?: string;
  children_info?: string;
  pets_info?: string;
  hobbies_interests?: string;
  talking_points?: string;
}

// Contact Task API
export const contactTaskAPI = {
  // List all contact tasks
  getAll: (params?: {
    my_tasks?: boolean;
    assigned_to?: number;
    completed?: boolean;
    task_type?: string;
    due_before?: string;
    due_after?: string;
    overdue?: boolean;
  }) => crmAPI.get<{ count: number; results: ContactTask[] }>('/crm/contact-tasks/', { params }),

  // Get single task
  getById: (id: number) => crmAPI.get<ContactTask>(`/crm/contact-tasks/${id}/`),

  // Create task
  create: (data: {
    customer_id: number;
    title: string;
    due_date: string;
    task_type?: string;
    description?: string;
    assigned_to?: number;
    priority?: number;
    related_order_id?: number;
  }) => crmAPI.post<{ success: boolean; id: number }>('/crm/contact-tasks/', data),

  // Update task
  update: (id: number, data: Partial<ContactTask>) => crmAPI.put<{ success: boolean }>(`/crm/contact-tasks/${id}/`, data),

  // Delete task
  delete: (id: number) => crmAPI.delete(`/crm/contact-tasks/${id}/`),

  // Complete task with contact log
  complete: (id: number, data?: {
    notes?: string;
    contact_type?: string;
    follow_up_needed?: boolean;
    follow_up_date?: string;
    follow_up_notes?: string;
  }) => crmAPI.post<{ success: boolean; contact_log_id?: number }>(`/crm/contact-tasks/${id}/complete/`, data),

  /** Close a task WITHOUT logging a contact. Completing writes a ContactLog, which sets
   *  Customer.last_contacted_at - so clearing a backlog by completing it marks those
   *  customers freshly contacted and empties the 30-Day No Contact list. */
  dismiss: (id: number, data: { reason: string; note?: string }) =>
    crmAPI.post<{
      success: boolean; dismissed: boolean; dismissed_at: string;
      dismissal_reason: string; dismissal_note: string;
      contact_log_created: boolean; last_contacted_at_changed: boolean;
    }>(`/crm/contact-tasks/${id}/dismiss/`, data),

  dismissalReasons: () =>
    crmAPI.get<{ value: string; label: string }[]>('/crm/contact-tasks/dismissal_reasons/'),

  // Get Monday task list (current user's tasks for this week)
  getMyMonday: () => crmAPI.get<{
    count: number;
    week_start: string;
    week_end: string;
    tasks: ContactTask[];
  }>('/crm/contact-tasks/my_monday/'),

  // Get customers not contacted in 30+ days
  getNoContact30: () => crmAPI.get<{
    count: number;
    customers: NoContactCustomer[];
  }>('/crm/contact-tasks/no_contact_30/'),

  // Update relationship intelligence fields on a customer
  updateCustomerRelationship: (customerId: number, data: RelationshipUpdate) =>
    crmAPI.patch<{ success: boolean; updated_fields: string[] }>(`/crm/customers/${customerId}/update_relationship/`, data),
};

// Contact Log API
export const contactLogAPI = {
  // List logs
  getAll: (params?: {
    customer_id?: number;
    contact_type?: string;
    contacted_by?: number;
    date_from?: string;
    date_to?: string;
    follow_up_needed?: boolean;
  }) => crmAPI.get<{ count: number; results: ContactLog[] }>('/crm/contact-logs/', { params }),

  // Get single log
  getById: (id: number) => crmAPI.get<ContactLog>(`/crm/contact-logs/${id}/`),

  // Create log
  create: (data: {
    customer_id: number;
    contact_type?: string;
    notes: string;
    subject?: string;
    email_subject?: string;
    email_body?: string;
    follow_up_needed?: boolean;
    follow_up_date?: string;
    follow_up_notes?: string;
  }) => crmAPI.post<{ success: boolean; id: number }>('/crm/contact-logs/', data),

  // Update log
  update: (id: number, data: Partial<ContactLog>) => crmAPI.put<{ success: boolean }>(`/crm/contact-logs/${id}/`, data),

  // Get customer history
  getCustomerHistory: (customerId: number) => crmAPI.get<{
    customer_id: number;
    count: number;
    logs: ContactLog[];
  }>('/crm/contact-logs/customer_history/', { params: { customer_id: customerId } }),
};

// Relationship Reminder API
export const reminderAPI = {
  // List reminders
  getAll: (params?: {
    my_reminders?: boolean;
    completed?: boolean;
    reminder_type?: string;
  }) => crmAPI.get<{ count: number; results: RelationshipReminder[] }>('/crm/reminders/', { params }),

  // Create reminder
  create: (data: {
    customer_id: number;
    title: string;
    reminder_date: string;
    reminder_type?: string;
    description?: string;
    is_recurring?: boolean;
    assigned_to?: number;
  }) => crmAPI.post<{ success: boolean; id: number }>('/crm/reminders/', data),

  // Complete reminder
  complete: (id: number) => crmAPI.post<{ success: boolean }>(`/crm/reminders/${id}/complete/`),

  // Get upcoming reminders (next 7 days)
  getUpcoming: (params?: { my_reminders?: boolean }) => crmAPI.get<{
    count: number;
    results: RelationshipReminder[];
  }>('/crm/reminders/upcoming/', { params }),
};

// Monday Digest API
export const mondayDigestAPI = {
  // Preview digest data for current user
  getPreview: () => crmAPI.get<MondayDigestPreview>('/crm/monday-digest/preview/'),

  // Send digest emails (admin only)
  send: (data?: {
    user_id?: number;
    dry_run?: boolean;
  }) => crmAPI.post<{ success: boolean; message: string; output: string }>('/crm/monday-digest/send/', data),

  // Generate contact tasks (admin only)
  generateTasks: (data?: {
    dry_run?: boolean;
  }) => crmAPI.post<{ success: boolean; message: string; output: string }>('/crm/monday-digest/generate_tasks/', data),
};

// ===== Booking Calendar API =====
export const bookingAPI = {
  // Admin appointments
  getAppointments: (params?: Record<string, string>) =>
    crmAPI.get('/admin/booking/appointments/', { params }),
  getCalendar: (month: string) =>
    crmAPI.get('/admin/booking/calendar/', { params: { month } }),
  updateAppointment: (id: string, data: Record<string, any>) =>
    crmAPI.put(`/admin/booking/appointments/${id}/`, data),
  completeAppointment: (id: string, data?: Record<string, any>) =>
    crmAPI.post(`/admin/booking/appointments/${id}/complete/`, data),
  noShowAppointment: (id: string) =>
    crmAPI.post(`/admin/booking/appointments/${id}/no-show/`),

  // Config
  getConfig: () => crmAPI.get('/admin/booking/config/'),
  updateConfig: (data: Record<string, any>) =>
    crmAPI.put('/admin/booking/config/', data),

  // Meeting types
  getMeetingTypes: () => crmAPI.get('/admin/booking/meeting-types/'),
  createMeetingType: (data: Record<string, any>) =>
    crmAPI.post('/admin/booking/meeting-types/', data),
  updateMeetingType: (id: string, data: Record<string, any>) =>
    crmAPI.put(`/admin/booking/meeting-types/${id}/`, data),
  deleteMeetingType: (id: string) =>
    crmAPI.delete(`/admin/booking/meeting-types/${id}/`),

  // Blockouts
  getBlockouts: () => crmAPI.get('/admin/booking/blockouts/'),
  createBlockout: (data: Record<string, any>) =>
    crmAPI.post('/admin/booking/blockouts/', data),
  deleteBlockout: (id: string) =>
    crmAPI.delete(`/admin/booking/blockouts/${id}/`),

  // Availability
  getAvailability: () => crmAPI.get('/admin/booking/availability/'),
  updateAvailability: (data: Record<string, any>[]) =>
    crmAPI.put('/admin/booking/availability/', data),

  // Teams integration
  testTeamsConnection: () =>
    crmAPI.post<{ success: boolean; message: string }>('/admin/booking/test-teams/'),
};

export default crmAPI;
